import React, { useState } from 'react';

import Table from 'cccisd-table';
import HiddenBlock from 'cccisd-hidden-block';
import Tooltip from 'cccisd-tooltip';
import _get from 'lodash/get';

import { getPlanStatus } from '../helpers.js';

import style from './style.css';

const isSupervisor = window.cccisd?.fortress?.user?.acting?.role?.handle === 'supervisor';

const YouthSelectionTable = props => {
    const { youthList, setSelectedYouth, youthSelectionTableSelectedRows, youthPlans } = props;

    const [selectedYouthPawnIds, setSelectedYouthPawnIds] = useState([]);
    // { [youthPawnIdString]: caregiverPawnId[] }
    const [selectedCaregivers, setSelectedCaregivers] = useState({});

    const buildAndSetSelectedYouth = (youthPawnIds, caregivers) => {
        const res = [];
        youthList.forEach(youth => {
            const pawnId = _get(youth, 'pawn.pawnId', '-1').toString();
            if (youthPawnIds.includes(pawnId)) {
                res.push({ youthPawnId: youth.pawn.pawnId, caregiverPawnIds: caregivers[pawnId] || [] });
            }
        });

        setSelectedYouth(res);
    };

    const toggleCaregiver = (youthPawnIdString, caregiverPawnId) => {
        const current = selectedCaregivers[youthPawnIdString] || [];
        const next = current.includes(caregiverPawnId)
            ? current.filter(id => id !== caregiverPawnId)
            : [...current, caregiverPawnId];
        const newCaregivers = { ...selectedCaregivers, [youthPawnIdString]: next };
        setSelectedCaregivers(newCaregivers);
        buildAndSetSelectedYouth(selectedYouthPawnIds, newCaregivers);
    };

    const DOES_NOT_RECEIVE_MESSAGES_COLOR = '#ff000052';

    const getInegligibleYouth = () => {
        let ineligible = youthList
            .map(youth => {
                let reason = '';

                const noneReceiveMessages =
                    !youth.fields.receiveMessages &&
                    youth.parentRoles.caregiverList.filter(caregiver => caregiver.fields.receiveMessages).length === 0;

                const youthActivePlan = _get(youthPlans, [youth.pawn.pawnId], []).filter(plan => {
                    const status = getPlanStatus(plan);
                    return status === 'Active' || status === 'Scheduled';
                });

                if (noneReceiveMessages) {
                    reason = 'Does not receive messages';
                }
                if (youthActivePlan.length > 0) {
                    if (reason.length > 0) {
                        reason += ' & ';
                    }
                    reason += 'Ongoing active plan';
                }

                return { ...youth, ineligible_reason: reason };
            })
            .filter(youth => youth.ineligible_reason);

        return ineligible;
    };

    return (
        <div>
            <Table
                rowKey="pawnId"
                selectedRows={youthSelectionTableSelectedRows}
                columns={[
                    {
                        name: 'wrapId',
                        label: 'Youth Wrap ID',
                        sort: true,
                        filter: true,
                    },
                    {
                        name: 'firstName',
                        label: 'Youth First Name',
                        sort: true,
                        filter: true,
                    },
                    {
                        name: 'lastName',
                        label: 'Youth Last Name',
                        sort: true,
                        filter: true,
                    },
                    {
                        name: 'receiveMessages',
                        label: 'Youth Receives Messages',
                        sort: true,
                        render: val => {
                            return (
                                <Tooltip
                                    title="Message settings can be changed for each user when editing them in the Manage tab."
                                    placement="top"
                                >
                                    <div
                                        style={{
                                            background: !val ? DOES_NOT_RECEIVE_MESSAGES_COLOR : 'yellowgreen',
                                        }}
                                        className={style.receive_messages_value_background}
                                    >
                                        {val ? 'Yes' : 'No'}
                                    </div>
                                </Tooltip>
                            );
                        },
                    },
                    {
                        name: 'youthStatus',
                        label: 'Youth Status',
                        sort: true,
                        filter: true,
                    },
                    {
                        name: 'caregiverList',
                        label: 'Caregiver(s)',
                        sort: true,
                        filter: true,
                        filterSettings: {
                            type: 'text',
                            custom: (row, value) => {
                                const caregivers = row.caregiverList || [];
                                const searchText = caregivers
                                    .map(
                                        caregiver =>
                                            `${caregiver.fields.firstName} ${caregiver.fields.lastName} ${caregiver.fields.wrapId}`
                                    )
                                    .join(' ');
                                return searchText.toLowerCase().includes(value.toLowerCase());
                            },
                        },
                        render: (val, row) => {
                            const youthPawnIdString = row.pawnId.toString();
                            const isYouthSelected = selectedYouthPawnIds.includes(youthPawnIdString);
                            const selectedCaregiverIds = selectedCaregivers[youthPawnIdString] || [];
                            return (
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {val.map((caregiver, i) => {
                                        const caregiverPawnId = caregiver.pawn.pawnId;
                                        return (
                                            <li
                                                key={i}
                                                style={{
                                                    marginBottom: i < val.length - 1 ? '0.5em' : 0,
                                                }}
                                            >
                                                <label
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: '6px',
                                                        cursor: isYouthSelected ? 'pointer' : 'default',
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCaregiverIds.includes(caregiverPawnId)}
                                                        disabled={!isYouthSelected}
                                                        onChange={() =>
                                                            toggleCaregiver(youthPawnIdString, caregiverPawnId)
                                                        }
                                                        style={{ marginTop: '3px', flexShrink: 0 }}
                                                    />
                                                    <div>
                                                        <div>
                                                            {`${caregiver.fields.firstName} ${caregiver.fields.lastName} (${caregiver.fields.wrapId})`}
                                                        </div>
                                                        <div>
                                                            Receives Messages:{' '}
                                                            <Tooltip
                                                                title="Message settings can be changed for each user when editing them in the Manage tab."
                                                                placement="top"
                                                            >
                                                                <span
                                                                    style={{
                                                                        background: !caregiver.fields.receiveMessages
                                                                            ? DOES_NOT_RECEIVE_MESSAGES_COLOR
                                                                            : 'yellowgreen',
                                                                    }}
                                                                    className={style.receive_messages_value_background}
                                                                >
                                                                    {caregiver.fields.receiveMessages ? 'Yes' : 'No'}
                                                                </span>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                </label>
                                            </li>
                                        );
                                    })}
                                </ul>
                            );
                        },
                    },
                    ...(isSupervisor
                        ? [
                              {
                                  name: 'careCoordinatorList',
                                  label: 'Care Coordinator',
                                  sort: true,
                                  filter: true,
                                  filterSettings: {
                                      type: 'text',
                                      custom: (row, value) => {
                                          const careCoordinators = row.careCoordinatorList || [];
                                          const searchText = careCoordinators
                                              .map(
                                                  careCoordinator =>
                                                      `${careCoordinator.user.fullName} ${careCoordinator.user.username}`
                                              )
                                              .join(' ');
                                          return searchText.toLowerCase().includes(value.toLowerCase());
                                      },
                                  },
                                  render: (val, row) => {
                                      const careCoordinatorList = _get(row, 'careCoordinatorList', []);

                                      if (careCoordinatorList.length === 0) {
                                          return '';
                                      }

                                      return careCoordinatorList.map(
                                          careCoordinator =>
                                              `${careCoordinator.user.fullName} (${careCoordinator.user.username})`
                                      );
                                  },
                              },
                          ]
                        : []),
                ]}
                // only include youth that are not in the ineligible list
                data={youthList
                    .filter(
                        youth =>
                            !getInegligibleYouth(youthList).some(
                                ineligible => ineligible.pawn.pawnId === youth.pawn.pawnId
                            )
                    )
                    .map(youth => {
                        return {
                            ...youth,
                            pawnId: youth.pawn.pawnId,
                            ...youth.fields,
                            ...youth.parentRoles,
                        };
                    })}
                isSimpleCheckboxes
                showRowActions={false}
                hideShowingRowsSummary
                showPerPageOptions={false}
                rowSelectedCallback={selected => {
                    // only process changes
                    if (
                        selected.length === selectedYouthPawnIds.length &&
                        selected.every(id => selectedYouthPawnIds.includes(id))
                    ) {
                        return;
                    }

                    setSelectedYouthPawnIds(selected);
                    const newCaregivers = {};
                    selected.forEach(pawnIdStr => {
                        // when selecting a youth, auto-select all caregivers by default
                        // include all caregivers, even if they have receiveMessages=false ; This is in case they decide to start sending messages in the middle of plan (e.g. got consent after week 2 - message will then start to be send but previous/missed messages will not be sent).
                        if (selectedCaregivers[pawnIdStr]) {
                            newCaregivers[pawnIdStr] = selectedCaregivers[pawnIdStr];
                        } else {
                            const youth = youthList.find(y => y.pawn.pawnId.toString() === pawnIdStr);
                            if (youth) {
                                newCaregivers[pawnIdStr] = youth.parentRoles.caregiverList.map(c => c.pawn.pawnId);
                            }
                        }
                    });
                    setSelectedCaregivers(newCaregivers);
                    buildAndSetSelectedYouth(selected, newCaregivers);
                }}
                rowActions={[
                    {
                        name: 'placeholder',
                        title: 'placeholder',
                        action: () => {},
                    },
                ]}
            />

            <br />

            <div key="added">
                <div className={style.tooltip_text}>
                    Youth who are ineligible to receive messages are listed in the table below.
                    <br />
                    Message settings can be changed on a per-user level in the{' '}
                    <a href="/manage">
                        <u>Manage</u>
                    </a>{' '}
                    page.
                </div>
                <HiddenBlock title="Ineligible">
                    <Table
                        rowKey="pawnId"
                        columns={[
                            {
                                name: 'wrapId',
                                label: 'Youth Wrap ID',
                                sort: true,
                                filter: true,
                            },
                            {
                                name: 'ineligible_reason',
                                label: 'Reason',
                                sort: true,
                                filter: true,
                            },
                            {
                                name: 'firstName',
                                label: 'Youth First Name',
                                sort: true,
                                filter: true,
                            },
                            {
                                name: 'lastName',
                                label: 'Youth Last Name',
                                sort: true,
                                filter: true,
                            },
                            {
                                name: 'receiveMessages',
                                label: 'Youth Receives Messages',
                                sort: true,
                                render: val => {
                                    return (
                                        <Tooltip
                                            title="Message settings can be changed for each user when editing them in the Manage tab."
                                            placement="top"
                                        >
                                            <div
                                                style={{
                                                    background: !val ? DOES_NOT_RECEIVE_MESSAGES_COLOR : 'yellowgreen',
                                                }}
                                                className={style.receive_messages_value_background}
                                            >
                                                {val ? 'Yes' : 'No'}
                                            </div>
                                        </Tooltip>
                                    );
                                },
                            },
                            {
                                name: 'youthStatus',
                                label: 'Youth Status',
                                sort: true,
                                filter: true,
                            },
                            {
                                name: 'caregiverList',
                                label: 'Caregiver(s)',
                                sort: true,
                                filter: true,
                                filterSettings: {
                                    type: 'text',
                                    custom: (row, value) => {
                                        const caregivers = row.caregiverList || [];
                                        const searchText = caregivers
                                            .map(
                                                caregiver =>
                                                    `${caregiver.fields.firstName} ${caregiver.fields.lastName} ${caregiver.fields.wrapId}`
                                            )
                                            .join(' ');
                                        return searchText.toLowerCase().includes(value.toLowerCase());
                                    },
                                },
                                render: val => {
                                    return (
                                        <ul>
                                            {val.map((caregiver, j) => (
                                                <li key={j}>
                                                    <div>
                                                        {`${caregiver.fields.firstName} ${caregiver.fields.lastName} (${caregiver.fields.wrapId})`}
                                                    </div>
                                                    <div>
                                                        Receives Messages:{' '}
                                                        <Tooltip
                                                            title="Message settings can be changed for each user when editing them in the Manage tab."
                                                            placement="top"
                                                        >
                                                            <span
                                                                style={{
                                                                    background: !caregiver.fields.receiveMessages
                                                                        ? DOES_NOT_RECEIVE_MESSAGES_COLOR
                                                                        : 'yellowgreen',
                                                                }}
                                                                className={style.receive_messages_value_background}
                                                            >
                                                                {caregiver.fields.receiveMessages ? 'Yes' : 'No'}
                                                            </span>
                                                        </Tooltip>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    );
                                },
                            },
                            ...(isSupervisor
                                ? [
                                      {
                                          name: 'careCoordinatorList',
                                          label: 'Care Coordinator',
                                          sort: true,
                                          filter: true,
                                          filterSettings: {
                                              type: 'text',
                                              custom: (row, value) => {
                                                  const careCoordinators = row.careCoordinatorList || [];
                                                  const searchText = careCoordinators
                                                      .map(
                                                          careCoordinator =>
                                                              `${careCoordinator.user.fullName} ${careCoordinator.user.username}`
                                                      )
                                                      .join(' ');
                                                  return searchText.toLowerCase().includes(value.toLowerCase());
                                              },
                                          },
                                          render: (val, row) => {
                                              const careCoordinatorList = _get(row, 'careCoordinatorList', []);

                                              if (careCoordinatorList.length === 0) {
                                                  return '';
                                              }

                                              return careCoordinatorList.map(
                                                  careCoordinator =>
                                                      `${careCoordinator.user.fullName} (${careCoordinator.user.username})`
                                              );
                                          },
                                      },
                                  ]
                                : []),
                        ]}
                        data={getInegligibleYouth(youthList).map(youth => {
                            return {
                                ...youth,
                                pawnId: youth.pawn.pawnId,
                                ...youth.fields,
                                ...youth.parentRoles,
                            };
                        })}
                        showRowActions={false}
                        hideShowingRowsSummary
                        showPerPageOptions={false}
                        rowActions={[]}
                    />
                </HiddenBlock>
            </div>
        </div>
    );
};

export default YouthSelectionTable;
